import {
  DEFAULT_GEO_LINESTRING_LINE_WIDTH,
  DEFAULT_GEO_LINESTRING_OPACITY,
} from '@racemap/utilities/consts/geoLineString';
import type { SplitObject } from '@racemap/utilities/types/geos';
import type { UnitType } from '@racemap/utilities/types/units';
import tinycolor from 'tinycolor2';
import { HOURS, SHADOW_TRACK_STEP } from './constants';
import type { ExternalLayerFeature, FeatureCollection, PoiFeature, TrackFeature } from './geojson';
import {
  type ChunkInfo,
  type EventChangeableState,
  type EventState,
  type ExternalLayerType,
  FlagContent,
  type Images,
  InfoSheetTabs,
  type MapType,
  type NetReplayModule,
  PinToShadowTrackMode,
  type PoiType,
  type StarterType,
  TimeMode,
  type TrackType,
} from './store';
import TrackView from './utils/TrackView';
import { correctedNow } from './utils/clock';
import type Option from './utils/option';

export type RawStarter = {
  id: string;
  name: string | null | undefined;
  startNumber: string | null | undefined;
  markerColor: string;
  deviceClass: string;
  tags?: Record<string, string>;
};

export type RawEventOptions = {
  stepWidth: number;
  markerTimeout: number;
  replaySpeedup: number;
  liveDelay: number;
  pollingInterval: number;
  mapType: MapType;
  unitType: UnitType;
  timeMode: TimeMode;
  showAllFlags: boolean;
  showDistanceMarkers: boolean;
  listOpenByDefault: boolean;
  defaultTagFilters?: Record<string, Array<{ value: string }>>;
  infoTabs: {
    openByDefault: boolean;
    defaultTab: InfoSheetTabs;
    disabledTabs: Array<InfoSheetTabs>;
  };
  pinToShadowTrack: PinToShadowTrackMode;
  tailTimeout: number;
  elevationChartOptions: null | {
    min: null | number;
    max: null | number;
  };
  flagContent: FlagContent;
  selectedFlagContent: FlagContent;
  viewport: null | {
    latitude: number;
    longitude: number;
    zoom: number;
    pitch: number;
    bearing: number;
  };
  filterTagKeys: Array<string> | null;
  visibleStarterGroups: Array<string> | null;
  reloadInterval: number;
};

type RawNetReplay =
  | { enabled: false }
  | {
      enabled: true;
      splitId: string;
      syncTime: string;
      sliderDuration: number;
    };

enum RawModuleState {
  NONE = 'NONE',
  ENABLED = 'ENABLED',
  ACTIVATED = 'ACTIVATED',
}

export type RawEvent = {
  id: string;
  name: string;
  slug: string;
  startTime: string | null;
  endTime: string | null;
  isRepeating: boolean;
  eventDescription: string;
  eventWebsite: string;
  eventContactMail: string;
  location: string;
  playerOptions: RawEventOptions;
  geo: {
    shadowtrackId: string | null;
    shadowgeojsonHash: string | null;
  };
  modules: {
    timing: {
      state: RawModuleState;
      leaderboard: {
        type: 'EXTERNAL' | 'DEFAULT';
        externalLeaderboardUrl: string;
      };
    };
    sponsor:
      | { state: RawModuleState.NONE }
      | {
          state: RawModuleState.ENABLED | RawModuleState.ACTIVATED;
          alignment: 'left' | 'center' | 'right' | 'space-between' | 'space-around';
          logos: Array<{
            id: string;
            url: string;
            title: string;
            imageUrls: Images | null;
            altText: string;
          }> | null;
        };
    nettoReplay: RawNetReplay;
    netReplay: RawNetReplay;
    projection: { enabled: boolean };
  };
  updatedAt: string;
  starters: Array<RawStarter>;
  startersCount: number;
  geoJSON: FeatureCollection;
  splits: Array<SplitObject>;
  shadowTrackIsVisible: boolean;
};
export type EventStateWithOptions = EventState & {
  globalOptions: {
    mapType: MapType;
    unitType: UnitType;
    timeMode: TimeMode;
    showAllFlags: boolean;
    pinToShadowTrack: PinToShadowTrackMode;
    largeMode: boolean;
    defaultTagFilters: Record<string, Array<{ value: string }>> | null;
  };
  changeable: EventChangeableState;
};

export type EventStateWithOptionsAndNulledChangeable = Omit<EventStateWithOptions, 'changeable'> & {
  changeable: null;
};

export function parseStarter(
  rawStarter: RawStarter,
  index: number,
  eventIndex: number,
  filterTagKeys: Array<string> | null = null,
): StarterType {
  const color = extractColor(rawStarter.markerColor);
  return {
    type: 'STARTER',
    index,
    id: rawStarter.id,
    eventIndex,
    name: rawStarter.name || undefined,
    startNumber: rawStarter.startNumber || undefined,
    searchText: `${rawStarter.name} ${rawStarter.startNumber}`,
    deviceClass: rawStarter.deviceClass,
    markerColor: color,
    tags:
      rawStarter.tags != null
        ? new Map(
            Object.entries(rawStarter.tags)
              .filter(([k]) => filterTagKeys == null || filterTagKeys.includes(k))
              .map(([k, v]) => [k, `${v}`.trim()] as [string, string])
              .filter(([, v]) => v !== ''),
          )
        : new Map(),
  };
}

export function parsePoi(rawPoi: PoiFeature, index: number, eventIndex: number): PoiType {
  const colorObj = extractColor(rawPoi.properties.color);

  const name = (rawPoi.properties.name ?? rawPoi.properties.title ?? '').trim();
  let shortName = rawPoi.properties.shortName?.trim();
  if (shortName === '') {
    shortName = name.substring(0, 6);
  }
  return {
    type: 'POI',
    index,
    id: `${rawPoi.properties.id ?? rawPoi.id}_${eventIndex}`,
    eventIndex,
    name,
    shortName,
    icon: rawPoi.properties.icon ?? undefined,
    latlng: { lng: rawPoi.geometry.coordinates[0], lat: rawPoi.geometry.coordinates[1] },
    offset: rawPoi.properties.offset,
    markerColor: colorObj,
  };
}

export function parseTrack(rawTrack: TrackFeature, index: number, eventIndex: number): TrackType {
  // TODO: replace input color array with rgba string or another more flexible format in the backend to avoid this complicated parsing logic
  const colorObj = extractColor(rawTrack.properties.color);
  return {
    type: 'TRACK',
    index,
    id: `${rawTrack.properties.id ?? rawTrack.id}_${eventIndex}`,
    eventIndex,
    name: rawTrack.properties.name ?? '',
    coordinates: rawTrack.geometry.coordinates,
    markerColor: colorObj,
    lineWidth: rawTrack.properties.lineWidth ?? DEFAULT_GEO_LINESTRING_LINE_WIDTH,
    opacity: rawTrack.properties.opacity ?? DEFAULT_GEO_LINESTRING_OPACITY,
  };
}

export function parseExternalLayer(
  rawExternalLayer: ExternalLayerFeature,
  index: number,
  eventIndex: number,
): ExternalLayerType {
  return {
    type: 'EXTERNAL_LAYER',
    index,
    id: `${rawExternalLayer.id}_${eventIndex}`,
    eventIndex,
    layerType: rawExternalLayer.properties.layerType,
    tiles: rawExternalLayer.properties.tiles,
    tileSize: rawExternalLayer.properties.tileSize,
  };
}

export function parseDate(input: string | number): number {
  const regexp =
    /(\d\d\d\d)(-)?(\d\d)(-)?(\d\d)([T ])?(\d\d)(:)?(\d\d)(:)?(\d\d)(\.\d+)?(Z|([+-])(\d\d)(:)?(\d\d))/;

  const inputString = input.toString();
  const d = inputString.match(regexp);
  if (d != null) {
    let offset = 0;

    const date = new Date(
      Date.UTC(
        Number.parseInt(d[1], 10),
        Number.parseInt(d[3], 10) - 1,
        Number.parseInt(d[5], 10),
        Number.parseInt(d[7], 10),
        Number.parseInt(d[9], 10),
        Number.parseInt(d[11], 10),
      ),
    );
    if (d[12]) {
      date.setUTCMilliseconds(Number.parseFloat(d[12]) * 1000);
    } else {
      date.setUTCMilliseconds(0);
    }

    if (d[13] !== 'Z') {
      offset = Number.parseInt(d[15], 10) * 60 + Number.parseInt(d[17], 10);
      offset *= d[14] === '-' ? -1 : 1;
      date.setTime(date.getTime() - offset * 60 * 1000);
    }
    return date.getTime();
  }
  return new Date(input).getTime();
}

export function parseChunks(buf: ArrayBuffer): Array<ChunkInfo> {
  const output = [];
  const view = new DataView(buf);
  let ptr = 0;
  while (ptr < buf.byteLength) {
    const chunkOffset = ptr + 8 + 8 + 4 + 4;
    const chunkStartTime = view.getFloat64(ptr, true);
    const chunkEndTime = view.getFloat64(ptr + 8, true);
    // encoded chunk length belongs to compressed chunk
    // so we have to subtract 4 bytes for the length itself
    const chunkLength = view.getUint32(ptr + 8 + 8, true) - 4;
    const encodedLength = view.getUint32(ptr + 8 + 8 + 4, true);
    const chunkInfo = {
      id: `${chunkStartTime}--${chunkEndTime}`,
      compressedBuffer: buf.slice(chunkOffset, chunkOffset + chunkLength),
      startTime: chunkStartTime,
      endTime: chunkEndTime,
      encodedLength,
      decodedLength: chunkLength,
    };
    output.push(chunkInfo);
    ptr += chunkLength + 8 + 8 + 4 + 4;
  }
  return output;
}

function parseNetReplay(
  rawNetReplay: RawNetReplay,
  rawEvent: RawEvent,
  chunkedShadowTrack: TrackView | null,
): NetReplayModule {
  if (
    rawEvent.startTime == null ||
    rawNetReplay == null ||
    !rawNetReplay.enabled ||
    rawNetReplay.splitId == null ||
    rawNetReplay.splitId === ''
  )
    return { enabled: false };

  const split = rawEvent.splits.find((split) => split.id === rawNetReplay.splitId);
  if (split == null) return { enabled: false };

  const shadowtrackDistance =
    chunkedShadowTrack != null ? chunkedShadowTrack.size() * SHADOW_TRACK_STEP : Number.NaN;
  const duration = rawNetReplay.sliderDuration;
  const syncTime =
    Date.parse(rawEvent.startTime) +
    duration * ((split.properties.offset || 0) / shadowtrackDistance);

  return {
    enabled: true,
    syncOffset: split.properties.offset || 0,
    syncTime: new Date(syncTime),
    sliderStartTime: new Date(rawEvent.startTime),
    sliderEndTime: new Date(Date.parse(rawEvent.startTime) + duration),
  };
}

export function parseEvent(
  eventIndex: number,
  rawEvent: RawEvent,
  chunkedShadowTrack: Option<ArrayBufferLike>,
): EventStateWithOptionsAndNulledChangeable {
  const { startTime, endTime, isRepeating, startersCount } = rawEvent;
  const shadowTrack = chunkedShadowTrack.map(parseChunkedShadowTrack).getNullable();
  const netReplay = parseNetReplay(
    rawEvent.modules.nettoReplay ?? rawEvent.modules.netReplay,
    rawEvent,
    shadowTrack,
  );

  let _startTime = correctedNow();
  let _endTime = _startTime + 1 * HOURS;
  if (startTime != null) {
    _startTime = parseDate(startTime);
  }
  if (endTime != null) {
    _endTime = parseDate(endTime);
  }
  if (netReplay.enabled && netReplay.sliderEndTime != null) {
    _endTime = netReplay.sliderEndTime.getTime();
  }

  if (isRepeating) {
    const eventDuration = _endTime - _startTime;
    const repeatTimeOffset =
      Math.floor((correctedNow() - _startTime) / eventDuration) * eventDuration;
    _startTime = _startTime + repeatTimeOffset;
    _endTime = _endTime + repeatTimeOffset;
  }

  return {
    eventId: rawEvent.id,
    index: eventIndex,
    name: rawEvent.name || '',
    shortName: rawEvent.name || '',
    slug: rawEvent.slug || rawEvent.id,
    startTime: _startTime,
    endTime: _endTime,
    eventDescription: rawEvent.eventDescription,
    eventWebsite: rawEvent.eventWebsite,
    eventContactMail: rawEvent.eventContactMail,
    eventLocation: rawEvent.location,
    updatedAt: new Date(rawEvent.updatedAt),
    shadowgeojsonHash: rawEvent.geo.shadowgeojsonHash ?? null,
    starters: rawEvent.starters.map((starter, index) =>
      parseStarter(starter, index, eventIndex, rawEvent.playerOptions.filterTagKeys),
    ),
    startersCount,
    pois: rawEvent.geoJSON.features
      .filter((f) => f.geometry?.type === 'Point')
      .map((f, index) => parsePoi(f as PoiFeature, index, eventIndex)),
    tracks: rawEvent.geoJSON.features
      .filter(
        (f) =>
          f.geometry?.type === 'LineString' &&
          !(f as TrackFeature).properties.hidden &&
          f.properties.racemapType === 'track',
      )
      .map((f, index) => parseTrack(f as TrackFeature, index, eventIndex)),
    externalLayers: rawEvent.geoJSON.features
      .filter(
        (f) =>
          f.properties.racemapType === 'externalLayer' &&
          f.properties.tiles != null &&
          f.properties.tiles.length > 0 &&
          f.properties.tiles.some((tileUrl) => tileUrl != null && tileUrl.trim() !== ''),
      )
      .map((f, index) => parseExternalLayer(f as ExternalLayerFeature, index, eventIndex)),
    options: {
      stepWidth: rawEvent.playerOptions.stepWidth,
      replaySpeedup: rawEvent.playerOptions.replaySpeedup,
      markerTimeout: rawEvent.playerOptions.markerTimeout,
      pollingInterval: rawEvent.playerOptions.pollingInterval,
      liveDelay: rawEvent.playerOptions.liveDelay,
      tailTimeout: rawEvent.playerOptions.tailTimeout ?? 1 * HOURS,
      elevationChartOptions: rawEvent.playerOptions.elevationChartOptions,
      viewport: rawEvent.playerOptions.viewport,
      flagContent: rawEvent.playerOptions.flagContent ?? FlagContent.STARTNUMBER,
      selectedFlagContent:
        rawEvent.playerOptions.selectedFlagContent ?? FlagContent.STARTNUMBER_AND_NAME,
      filterTagKeys: rawEvent.playerOptions.filterTagKeys,
      visibleStarterGroups: rawEvent.playerOptions.visibleStarterGroups ?? [],
      listOpenByDefault: rawEvent.playerOptions.listOpenByDefault ?? true,
      infoTabs: {
        openByDefault: rawEvent.playerOptions.infoTabs?.openByDefault ?? true,
        defaultTab: rawEvent.playerOptions.infoTabs?.defaultTab ?? InfoSheetTabs.ELEVATION,
        disabledTabs: rawEvent.playerOptions.infoTabs?.disabledTabs ?? [],
      },
      showDistanceMarkers: rawEvent.playerOptions.showDistanceMarkers,
      reloadInterval: rawEvent.playerOptions.reloadInterval ?? 0,
    },
    modules: {
      timing:
        rawEvent.modules.timing.state !== RawModuleState.NONE
          ? {
              enabled: true,
              leaderboardUrl:
                rawEvent.modules.timing.leaderboard.type === 'EXTERNAL'
                  ? rawEvent.modules.timing.leaderboard.externalLeaderboardUrl
                  : `/leaderboard/${rawEvent.slug}?embed=true`,
            }
          : { enabled: false },

      sponsor:
        rawEvent.modules.sponsor.state !== RawModuleState.ACTIVATED
          ? { enabled: false }
          : {
              enabled: true,
              alignment: rawEvent.modules.sponsor.alignment,
              logos: rawEvent.modules.sponsor.logos,
            },
      netReplay,
      projection: rawEvent.modules.projection,
    },
    globalOptions: {
      mapType: rawEvent.playerOptions.mapType,
      unitType: rawEvent.playerOptions.unitType,
      timeMode: rawEvent.playerOptions.timeMode ?? TimeMode.RELATIVE,
      showAllFlags: rawEvent.playerOptions.showAllFlags,
      pinToShadowTrack: rawEvent.modules.projection.enabled
        ? PinToShadowTrackMode.FORCED
        : rawEvent.playerOptions.pinToShadowTrack,
      largeMode: rawEvent.playerOptions.largeMode ?? false,
      defaultTagFilters: rawEvent.playerOptions.defaultTagFilters || null,
    },
    changeable: null,
    shadowTrackIsVisible: shadowTrack != null && rawEvent.shadowTrackIsVisible,
    chunkedShadowTrack: shadowTrack,
  };
}

export function parseChunkedShadowTrack(buffer: ArrayBufferLike): TrackView {
  return new TrackView(new Float64Array(buffer));
}

function extractColor(color: string | Array<number>): {
  hex: string;
  hexNum: number;
  isLight: boolean;
  rgba: string;
} {
  const colorParsed =
    color != null
      ? Array.isArray(color)
        ? tinycolor({
            r: color[0],
            g: color[1],
            b: color[2],
            a: color[3] / 255,
          })
        : tinycolor(color)
      : null;

  return {
    hex: colorParsed?.toHexString() ?? '#ffffff',
    hexNum: Number.parseInt(colorParsed?.toHex() ?? 'ffffff', 16),
    isLight: colorParsed?.isLight() ?? true,
    rgba: colorParsed?.toRgbString() ?? 'rgba (255, 255, 255, 1)',
  };
}
